import { useEffect, useMemo, useState } from "react";
import { Sparkles, Check, X } from "lucide-react";
import { track } from "@/lib/analytics";
import { Confetti } from "@/components/Confetti";
import { useUserXp } from "@/hooks/use-user-xp";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

const XP_PER_ANSWER = 25;
const VERDICT: Record<string, [string, string]> = {
  en: ["Correct.", "Not this time."], fr: ["Bonne réponse.", "Raté."],
  es: ["¡Correcto!", "Fallaste."], pt: ["Correto!", "Errou."],
  de: ["Richtig!", "Leider falsch."], sv: ["Rätt!", "Fel svar."],
  ru: ["Верно!", "Неверно."], zh: ["答对了。", "答错了。"],
  hi: ["सही जवाब!", "गलत जवाब।"], ar: ["إجابة صحيحة!", "إجابة خاطئة."],
};

/**
 * The Analyst Call.
 *
 * Sits between the article promise and the story player, so every visitor
 * makes one quick call before watching or reading the evidence.
 *
 * Two modes:
 *  1. Knowledge check - when this discovery has its OWN quiz (a question
 *     written from this article's body). Real question, real answer, real
 *     explanation. Never a quiz borrowed from another article.
 * Articles without a saved question omit this block. The publishing worker
 * creates the grounded question; generic filler must never reach visitors.
 */

export type ArticleQuiz = {
  question: string;
  options: string[];
  answer_index: number;
  explanation: string;
};


export function AnalystCall({
  slug,
  answered,
  onAnswer,
  quiz,
}: {
  slug: string;
  answered: "real" | "fake" | undefined;
  onAnswer: (sharp: boolean) => void;
  quiz?: ArticleQuiz | null;
}) {
  if (quiz && Array.isArray(quiz.options) && quiz.options.length >= 2) {
    return <KnowledgeCheck slug={slug} quiz={quiz} answered={answered} onAnswer={onAnswer} />;
  }
  return null;
}

/** Real question, written from this article. Options are always shuffled. */
function KnowledgeCheck({
  slug,
  quiz,
  answered,
  onAnswer,
}: {
  slug: string;
  quiz: ArticleQuiz;
  answered: "real" | "fake" | undefined;
  onAnswer: (sharp: boolean) => void;
}) {
  // Shuffling during render breaks SSR hydration (server and client draw
  // different orders), so the server renders the natural order and the shuffle
  // happens once on mount. Answer order stays randomised for every reader.
  const identity = useMemo(
    () => quiz.options.map((_, i) => i),
    [quiz.question, quiz.options.length],
  );
  const [order, setOrder] = useState<number[]>(identity);
  useEffect(() => {
    const o = [...identity];
    for (let i = o.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [o[i], o[j]] = [o[j]!, o[i]!];
    }
    setOrder(o);
  }, [identity]);
  const [picked, setPicked] = useState<number | null>(null);
  const done = picked !== null || !!answered;
  const correct = picked !== null && order[picked] === quiz.answer_index;
  const { awardQuiz } = useUserXp();
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const verdict = VERDICT[lang] ?? VERDICT.en!;
  const [gained, setGained] = useState(0);

  const choose = (i: number) => {
    if (done) return;
    try { navigator.vibrate?.([12, 40, 18]); } catch { /* unsupported */ }
    setPicked(i);
    const isRight = order[i] === quiz.answer_index;
    track("Analyst Call Answer", { slug, choice: quiz.options[order[i]], sharp: isRight });
    onAnswer(isRight);
    if (isRight) setGained(awardQuiz(`article:${slug}`, XP_PER_ANSWER) || XP_PER_ANSWER);
  };

  return (
    <section className="relative mb-5 overflow-hidden rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 md:p-6">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <Sparkles className="h-3.5 w-3.5" /> {ui("Knowledge check")}
      </div>
      <p className="mt-3 font-display text-lg uppercase leading-tight text-foreground md:text-2xl">
        {quiz.question}
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {order.map((origIdx, i) => {
          const isAns = origIdx === quiz.answer_index;
          const isPicked = picked === i;
          return (
            <button
              key={`${slug}-${origIdx}`}
              type="button"
              onClick={() => choose(i)}
              disabled={done}
              className={`flex items-center gap-2 rounded-2xl border px-4 py-3 text-left text-sm font-bold transition ${
                !done
                  ? "border-border bg-background hover:border-primary hover:bg-primary/10 active:scale-[0.98]"
                  : isAns
                    ? "border-primary bg-primary/10 text-primary"
                    : isPicked
                      ? "border-destructive/60 bg-destructive/10"
                      : "border-border bg-background/40 text-muted-foreground opacity-60"
              }`}
            >
              {done && isAns && <Check className="h-4 w-4 shrink-0" />}
              {done && isPicked && !isAns && <X className="h-4 w-4 shrink-0" />}
              <span>{quiz.options[origIdx]}</span>
            </button>
          );
        })}
      </div>
      {picked !== null && correct && <div className="pointer-events-none fixed inset-0 z-[90]"><Confetti pieces={70} /></div>}
      {picked !== null && correct && gained > 0 && (
        <div className="mt-4 inline-flex animate-bounce items-center gap-1 rounded-full bg-primary px-4 py-1.5 font-display text-lg text-primary-foreground shadow-lg" role="status">
          +{gained} XP
        </div>
      )}
      {picked !== null && (
        <p className="mt-4 text-sm text-muted-foreground">
          <span className={`font-bold ${correct ? "text-primary" : "text-foreground"}`} data-no-translate>
            {correct ? verdict[0] : verdict[1]}{" "}
          </span>
          {quiz.explanation}
        </p>
      )}
    </section>
  );
}
